import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * sessionStorage key for the last-viewed hole on the score page.
 * Scoped by player nano-id so a phone shared between players doesn't
 * cross-contaminate. sessionStorage (not local) so the value lives only
 * for the tab — sensible default if the player opens the app fresh.
 */
const lastHoleKey = (playerKey: string) => `polf:score:lastHole:${playerKey}`

const readLastHole = (playerKey: string): number | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(lastHoleKey(playerKey))
    if (!raw) return null
    const n = parseInt(raw, 10)
    if (!Number.isFinite(n) || n < 1 || n > 18) return null
    return n
  } catch {
    return null
  }
}

const writeLastHole = (playerKey: string, hole: number) => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(lastHoleKey(playerKey), String(hole))
  } catch {
    // Ignore quota / private-mode errors.
  }
}

/**
 * Hole-selector hook for the score page.
 *
 * Resolution order:
 *   1. ?hole=N in the URL  (deep links + explicit nav win)
 *   2. sessionStorage      (tab restored / cross-tab nav back)
 *   3. the flight's start hole  (shotgun starts open on their own hole,
 *      not hole 1)
 *
 * Backfills the URL with the resolved value on first paint so a shared
 * link to /score still reads `?hole=N` after restore. Persists every
 * change to sessionStorage so a refresh lands on the same hole.
 *
 * Returns the current hole and a setter that updates BOTH the URL and
 * scrolls the page back to the top (so the captain doesn't see the
 * previous hole's player cards mid-view).
 */
export const useHoleSelector = (
  playerKey: string,
  startHole = 1
): { currentHole: number; setHole: (n: number) => void } => {
  const [params, setParams] = useSearchParams()
  const urlHole = parseInt(params.get('hole') ?? '', 10)
  const currentHole = useMemo(() => {
    if (Number.isFinite(urlHole) && urlHole >= 1 && urlHole <= 18) {
      return urlHole
    }
    return readLastHole(playerKey) ?? startHole
  }, [urlHole, playerKey, startHole])

  useEffect(() => {
    if (!Number.isFinite(urlHole) || urlHole < 1 || urlHole > 18) {
      setParams(
        (p) => {
          const next = new URLSearchParams(p)
          next.set('hole', String(currentHole))
          return next
        },
        { replace: true }
      )
    }
    writeLastHole(playerKey, currentHole)
  }, [currentHole, playerKey, urlHole, setParams])

  const setHole = useCallback(
    (n: number) => {
      setParams((p) => {
        const next = new URLSearchParams(p)
        next.set('hole', String(n))
        return next
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [setParams]
  )

  return { currentHole, setHole }
}
